export const assetUrl = process.env.NEXT_PUBLIC_WIDGET_ASSET_BASE_URL ?? (process.env.NEXT_PUBLIC_APP_URL ?? (process.env.NEXT_PUBLIC_VERCEL_URL ?? 'http://localhost:3000'));

// Plugin assets are served from the same CDN as system widgets via `/p/`
// (sibling of `/w/`) — same origin, single CSP entry. `proxyBaseUrl`
// retained for legacy override; defaults to `assetUrl`.
export const proxyBaseUrl =
    process.env.NEXT_PUBLIC_PROXY_BASE_URL ??
    process.env.NEXT_PUBLIC_WIDGET_ASSET_BASE_URL ??
    assetUrl;

export const getAssetUrl = (path: string) => {
    return assetUrl + '/' + (path.startsWith('/') ? path.substring(1) : path);
}

/**
 * Formats a price in the visitor's locale using the NARROW currency symbol —
 * plain "$200" instead of "US$200" when a non-US English locale (en-GB, en,
 * en-AU…) formats USD (#599). Falls back to the wide symbol where
 * narrowSymbol is unsupported, then to `CODE amount` for unknown currencies.
 */
export function formatPrice(amount: number, currency: string, locale?: string, fractionDigits = 0): string {
    const opts: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    };
    try {
        return new Intl.NumberFormat(locale, { ...opts, currencyDisplay: 'narrowSymbol' }).format(amount);
    } catch {
        try {
            return new Intl.NumberFormat(locale, opts).format(amount);
        } catch {
            return `${currency} ${Math.round(amount).toLocaleString()}`;
        }
    }
}

/**
 * Pull a human-readable string out of a Central public-API error body so the guest sees
 * what actually failed instead of a generic message or "[object Object]" (#624, #626).
 *
 * The API returns errors as `{ error: { code, message, details } }`, where `details` is a
 * Laravel-style validation map (`{ field: [messages] }`); older/plain responses may carry a
 * top-level string `message`/`error`. Prefers the specific validation message(s), then the
 * generic error message, then a top-level string, then the caller's fallback — and never
 * returns an object stringified to "[object Object]".
 */
export function extractApiErrorMessage(data: unknown, fallback: string): string {
    const d = data as {
        message?: unknown
        error?: unknown | { message?: unknown; details?: unknown }
        details?: unknown
    } | null | undefined
    const errObj = (d && typeof d.error === 'object' && d.error !== null)
        ? (d.error as { message?: unknown; details?: unknown })
        : undefined
    const details = errObj?.details ?? d?.details
    if (details && typeof details === 'object') {
        const messages = Object.values(details as Record<string, unknown>)
            .flat()
            .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
        if (messages.length) return messages.join(' ')
    }
    for (const c of [errObj?.message, d?.message, d?.error]) {
        if (typeof c === 'string' && c.trim().length > 0) return c
    }
    return fallback
}

export const PLUGIN_WIDGET_PREFIX = 'plugin:';

// Keyword parsing + per-widget file naming for single- AND multi-widget
// plugins (`plugin:{slug}` / `plugin:{slug}:{widget}`) — the shared helpers
// own both shapes (docs/plugin-system/multi-widget-plugins.md).
export { parsePluginKeyword, pluginWidgetDistFile } from '@homerunner-next/widget-core/urls';
import { parsePluginKeyword, pluginWidgetDistFile } from '@homerunner-next/widget-core/urls';

/**
 * Build an asset-proxy URL for a plugin's compiled file.
 * Uses the stable `/p/{slug}/{file}` path which 302s in a single hop to the
 * author's CDN hashed file. Filename convention is `{slug}.css` /
 * `{slug}.iife.js` / `{slug}-ssr.umd.js` — matches create-hr-plugin output.
 */
export function getPluginAssetUrl(slug: string, file: string): string {
    const base = proxyBaseUrl.endsWith('/') ? proxyBaseUrl.slice(0, -1) : proxyBaseUrl;
    return `${base}/p/${slug}/${file}`;
}

// --- Manifest support for content-hashed assets ---

export interface AssetManifest {
    buildHash: string;
    generatedAt: string;
    files: Record<string, string>;
}

let _manifest: AssetManifest | null = null;

/**
 * Call this from the renderer after fetching the manifest via HTTP.
 * Once set, getDefaultCSSUrls/getDefaultJsUrls will return hashed file paths.
 * If never called (CSR), they return /w/ proxy paths instead.
 *
 * Note: This is module-level global state shared across concurrent requests.
 * Safe because: the renderer's getManifest() has a 60s in-memory cache, so all
 * requests within that window see the same manifest. The race window (TTL expiry
 * mid-request) is <1s vs 60s TTL — negligible in practice.
 */
export function setManifest(manifest: AssetManifest): void {
    _manifest = manifest;
}

/**
 * Resolve a dist-relative asset to its URL: content-hashed when the manifest is
 * set (SSR), the /w/ 302 proxy otherwise (browser). Exported for stylesheets
 * that live outside the per-widget defaults (e.g. self-hosted leaflet.css).
 */
export function resolveAssetPath(distRelPath: string): string {
    if (_manifest?.files[distRelPath]) {
        return getAssetUrl(`dist/${_manifest.files[distRelPath]}`);
    }
    return getAssetUrl(`w/${distRelPath}`);
}

/**
 * Google Fonts stylesheet URL for a widget's configured family. `display=swap`
 * so text paints immediately in a fallback font instead of FOIT-blanking for
 * up to ~3s on cold font caches (#787) — this URL lands both in the page head
 * and inside every widget shadow root, where the reveal gates on it.
 */
export function getFontCssUrl(family: string | null | undefined): string {
    // The renderer calls getStaticAssets with RAW stored settings (no schema
    // runs in getFinalSettings), so `family` can arrive null (Laravel
    // ConvertEmptyStringsToNull) or '' (legacy blobs) — which built a broken
    // `?family=null` stylesheet or threw upstream (#1171 review). Fall back to
    // the schema default: the parsed widget renders with Inter in exactly
    // these cases, so this keeps the shipped stylesheet and the rendered font
    // in agreement.
    const resolved = typeof family === "string" && family !== "" ? family : "Inter";
    return `https://fonts.googleapis.com/css?family=${encodeURIComponent(resolved)}:400,500,600,700&display=swap`;
}

/**
 * Returns default CSS URLs for a widget type.
 * If manifest is set (via setManifest): uses content-hashed filenames for immutable caching.
 * Otherwise: uses /w/ proxy URLs that 302-redirect to the current hashed file.
 * Plugin widgets (widgetType starts with `plugin:`) resolve to the dashboard
 * asset proxy instead of the embeddables `/w/` base.
 */
export function getDefaultCSSUrls(widgetType: string): string[] {
    if (widgetType.startsWith(PLUGIN_WIDGET_PREFIX)) {
        const parts = parsePluginKeyword(widgetType);
        if (!parts) return [];
        return [getPluginAssetUrl(parts.pluginSlug, pluginWidgetDistFile(parts, 'css'))];
    }
    return [
        getAssetUrl("icomoon/icomoon.css"),
        resolveAssetPath("common.css"),
        resolveAssetPath(`${widgetType}/${widgetType}.css`),
    ];
}

/**
 * Returns default JS URLs for a widget type.
 * If manifest is set (via setManifest): uses content-hashed filenames for immutable caching.
 * Otherwise: uses /w/ proxy URLs that 302-redirect to the current hashed file.
 * Plugin widgets resolve to the dashboard asset proxy. Runtime itself is still
 * served from the embeddables base — shared across all widget types.
 */
export function getDefaultJsUrls(widgetType: string): string[] {
    if (widgetType.startsWith(PLUGIN_WIDGET_PREFIX)) {
        const parts = parsePluginKeyword(widgetType);
        if (!parts) return [resolveAssetPath("runtime.iife.js")];
        return [
            resolveAssetPath("runtime.iife.js"),
            getPluginAssetUrl(parts.pluginSlug, pluginWidgetDistFile(parts, 'iife')),
        ];
    }
    return [
        resolveAssetPath("runtime.iife.js"),
        resolveAssetPath(`${widgetType}/${widgetType}.iife.js`)
    ];
}
